"use client";
import { useMemo } from "react";
import { LMS_DOMAIN } from "@/lib/course/utils/constants";
import { DEFAULT_AUTH_MODE, useAuthBaseURL } from "@/hooks/useAuthBaseURL";
import getTrackSelectionUrl from "@/components/CourseContent/ContentRefresh/CourseEnroll/utils/getTrackSelectionUrl";
import useInternalTrackSelect from "@/monarch/rules/trackSelection/useInternalTrackSelect";
import useCourseDetailsData from "@/components/CourseContent/CourseContext/hooks/useCourseDetailsData";
import { useLogistrationModal } from "@/components/LogistrationModal/hooks/useLogistrationModal";
import useWebview from "@/webview/useWebview";
import { CourseRun } from "@/common/courseRun/types";
import { Course, ProcessedCourse } from "@/common/course/types";
import { getCommerceUrlBase } from "@/utils/functions/commerce";
import { buildSafeUrlWithBase } from "@/utils/functions/buildSafeUrl";
import { CommercePath } from "@/hooks/useBuildCommerceUrl";
import useMarketplaceAttributionHref from "@/hooks/useMarketplaceAttributionHref";

interface UseEnrollmentUrlProps {
  courseRun: CourseRun;
  course: Course | ProcessedCourse;
  emailOptIn?: boolean;
  commerceCoordinatorUrl?: string;
  linkDirectToPaymentPage?: boolean;
  straightToCheckout?: boolean;
  isLoggedIn: boolean;
  isCourseEnrollable: boolean | null;
  isCourseArchived: boolean | null;
}

interface UseEnrollmentUrlReturn {
  authUrl: string;
}

/**
 * Shared hook for constructing enrollment URLs used by EnrollLink.
 * Handles track selection URL logic and auth URL construction.
 */
export function useEnrollmentUrl({
  courseRun,
  course,
  emailOptIn = false,
  commerceCoordinatorUrl,
  linkDirectToPaymentPage = false,
  straightToCheckout = false,
  isLoggedIn,
  isCourseEnrollable,
  isCourseArchived,
}: UseEnrollmentUrlProps): UseEnrollmentUrlReturn {
  const authBaseUrl = useAuthBaseURL();
  const { isWebview } = useWebview();
  const { variant } = useInternalTrackSelect(course as ProcessedCourse);
  const { courseDetailData } = useCourseDetailsData();
  const { shouldShowModal } = useLogistrationModal();

  const showInternalTrackSelect = variant === "test";
  const { category, courseSlug } = courseDetailData;

  const { authUrl } = useMemo(() => {
    let authUrl: string;

    if (
      (linkDirectToPaymentPage || straightToCheckout) &&
      commerceCoordinatorUrl
    ) {
      if (straightToCheckout && !isLoggedIn) {
        const sku =
          courseRun.seats?.find((s) => s.type === "verified")?.sku ??
          courseRun.seats?.find((s) => s.type === "professional")?.sku;

        if (!sku) {
          authUrl = commerceCoordinatorUrl;
        } else {
          const rawCommerceUrl = buildSafeUrlWithBase(
            getCommerceUrlBase(),
            CommercePath.PaymentRedirect,
            {
              sku,
              course_run_key: courseRun.key,
              straightToCheckout: "true",
            },
          );
          authUrl = new URL(
            `${DEFAULT_AUTH_MODE}?next=${encodeURIComponent(rawCommerceUrl)}`,
            authBaseUrl,
          ).toString();
        }
      } else {
        authUrl = commerceCoordinatorUrl;
      }
    } else {
      // ALL cases need a `next` parameter
      const nextUrl = `${LMS_DOMAIN}/course_modes/choose/${encodeURIComponent(courseRun.key)}`;

      authUrl = new URL(
        `${DEFAULT_AUTH_MODE}?course_id=${encodeURIComponent(courseRun.key)}&enrollment_action=enroll&email_opt_in=${emailOptIn}&next=${encodeURIComponent(nextUrl)}`,
        authBaseUrl,
      ).toString();
    }

    // Override for test group (non-webview only)
    // Skip if straightToCheckout
    if (
      showInternalTrackSelect &&
      !isWebview &&
      !isCourseArchived &&
      isCourseEnrollable === true &&
      !straightToCheckout &&
      !linkDirectToPaymentPage
    ) {
      authUrl = getTrackSelectionUrl({
        courseRunKey: courseRun.key,
        category,
        courseSlug,
        useNewTrackSelect: showInternalTrackSelect,
        emailOptIn,
        authBaseUrl,
        isUserLoggedIn: isLoggedIn,
        byPassAuth: shouldShowModal,
      });
    }

    return {
      authUrl,
    };
  }, [
    courseRun.key,
    courseRun.seats,
    course,
    emailOptIn,
    commerceCoordinatorUrl,
    linkDirectToPaymentPage,
    straightToCheckout,
    isLoggedIn,
    isCourseEnrollable,
    isCourseArchived,
    authBaseUrl,
    isWebview,
    showInternalTrackSelect,
    category,
    courseSlug,
    shouldShowModal,
  ]);

  return {
    authUrl: useMarketplaceAttributionHref(authUrl, {
      includeNextDestination: true,
    }),
  };
}
